import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Post } from '../../../types/models';
import { getTranslatedBlogContent } from '../data/blogTranslations';

interface BlogCardProps {
  post: Post;
}

const BlogCard: React.FC<BlogCardProps> = ({ post }) => {
  const { t } = useTranslation();

  const { i18n } = useTranslation();
  const translatedContent = getTranslatedBlogContent(post.slug || '', i18n.language);
  const displayTitle = translatedContent?.title || post.title;
  const displayExcerpt = translatedContent?.excerpt || post.excerpt;
  const isRTL = i18n.language === 'ar';

  return (
    <Card
      dir={isRTL ? 'rtl' : 'ltr'}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -5 }}
    >
      <ImageContainer>
        <img
          src={post.featuredImage || '/images/placeholder.jpg'}
          alt={displayTitle}
          loading="lazy"
        />
      </ImageContainer>

      <Content>
        <TitleAccent
          initial={{ width: 0 }}
          animate={{ width: '100%' }}
          transition={{ duration: 2, ease: 'easeOut' }}
        />
        <Title>{displayTitle}</Title>
        <Excerpt>{displayExcerpt}</Excerpt>

        <ReadMoreButton to={`/blog/${post.slug?.trim()}`}>
          <strong style={{color:'white'}}>{t('blog.readMore')}</strong>
        </ReadMoreButton>
      </Content>
    </Card>
  );
};

const Card = styled(motion.div)`
  background: var(--card-bg);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  overflow: hidden;
  transition: var(--transition);
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 520px;

  &:hover {
    box-shadow: var(--shadow-lg);
  }

  @media (max-width: 768px) {
    min-height: 0;
  }
`;

const ImageContainer = styled.div`
  height: 200px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;

    ${Card}:hover & {
      transform: scale(1.05);
    }
  }
`;

const Content = styled.div`
  padding: var(--spacing-lg);
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 320px;
`;

const TitleAccent = styled(motion.div)`
  height: 4px;
  background: var(--secondary);
  border-radius: 999px;
  margin-bottom: var(--spacing-md);
  transform-origin: inline-start center;
`;

const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: var(--spacing-md);
  line-height: 1.3;
  min-height: 4.9rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    min-height: auto;
  }
`;

const Excerpt = styled.p`
  color: var(--gray);
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: var(--spacing-lg);
  flex: 1;
  min-height: 4.6rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ReadMoreButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--primary);
  color: var(--white);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  transition: var(--transition);
  align-self: flex-start;

  [dir='rtl'] & {
    align-self: flex-start;
  }

  &:hover {
    background: var(--primary-dark);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default BlogCard;
